// Seegang in der Fahrt (Erste Fassung, Runde 8; Fabi 20.10.2026: „ab Nah soll das Wasser zunehmend rauer werden,
// wirklich welliger, nicht nur schneller“): Wellenzüge, Täler, Rücken, Kämme und Schaum über dem Wasserbild eines Orts,
// nach den Hinweisen „Seegang“ in public/art/fahrt/fahrt-spiel.json des Grafik-Leads. Dazu der Dunst am Horizont und die
// Wasserkante am Rumpf, wenn das Boot eintaucht. Jeder Ort hat seine eigene Leinwand und seine eigenen Phasen; neu
// gerechnet wird höchstens alle seegang.takt ms, dazwischen wird das letzte Bild mit dem Wasser verschoben.
import { img, type Durchfahrt, type Ort, type Seegang } from './data.ts';

type RGB = [number, number, number];

const BAYER4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];
/** So viele Pixel rechnet die Leinwand links und rechts über das Bild hinaus, damit beim Verschieben nichts fehlt */
const MARGIN = 40;
/** Bis zu dieser Zeile reicht das Wasser höchstens (Hinweis „Seegang“) */
const BOTTOM = 356;

type SeaState = {
  /** Phasen der Wellenzüge, der Windzug und die Gruppenphase laufen mit der Zeit, je Ort, nie zurückgesetzt */
  ph: number[];
  drift: number;
  gph: number;
  lastNow: number;
  /** Zeilen: Maßstab p, Tiefe v, Zug */
  hor: number;
  p: Float32Array;
  v: Float32Array;
  zug: Float32Array;
  canvas: HTMLCanvasElement | null;
  /** hn je Pixel der letzten Rechnung, für die Wasserkante am Rumpf */
  hn: Float32Array | null;
  /** Wann zuletzt gerechnet wurde und wo die Zeile bei 80 % der Wasserhöhe da war */
  doneAt: number;
  done80: number;
  shift: number;
};

const states = new Map<string, SeaState>();

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const mix = (a: RGB, b: RGB, w: number): RGB => [a[0] + (b[0] - a[0]) * w, a[1] + (b[1] - a[1]) * w, a[2] + (b[2] - a[2]) * w];
export const hexRgb = (hex: string): RGB => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];

function rowsOf(st: SeaState, hor: number, sg: Seegang): void {
  if (st.hor === hor && st.p.length) return;
  const y0 = hor + 2;
  const n = Math.max(2, BOTTOM - y0 + 1);
  const [p0, p1, pe] = sg.massstab;
  st.hor = hor;
  st.p = new Float32Array(n);
  st.v = new Float32Array(n);
  st.zug = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const d = i / (n - 1);
    st.p[i] = p0 + p1 * d ** pe;
    st.zug[i] = 0.28 + 0.72 * d;
  }
  // Die Tiefe v wird von unten nach oben aufsummiert
  for (let i = n - 2; i >= 0; i--) st.v[i] = st.v[i + 1]! + sg.dv / st.p[i]! ** 2;
  st.canvas = null;
  st.hn = null;
  st.doneAt = -1e9;
}

function stateOf(id: string, sg: Seegang): SeaState {
  let st = states.get(id);
  if (!st) {
    st = { ph: sg.zuege.map(() => 0), drift: 0, gph: 0, lastNow: NaN, hor: -1, p: new Float32Array(0), v: new Float32Array(0), zug: new Float32Array(0), canvas: null, hn: null, doneAt: -1e9, done80: 0, shift: 0 };
    states.set(id, st);
  }
  return st;
}

/** Mittlere Farbe des Wassers eines Orts (Zeilen hor + 2 bis hor + 50), normal oder im Sturm, zwischengespeichert */
const waterMeans = new Map<string, RGB | null>();
export function waterColor(d: Durchfahrt, o: Ort, storm: boolean): RGB | null {
  const key = `${o.id}|${storm}`;
  if (waterMeans.has(key)) return waterMeans.get(key)!;
  const at = d.atlas[o.id];
  const im = at ? img(`fahrt/${at.datei}`) : null;
  const part = at?.teile[`${o.id}_wasser${storm ? '_sturm' : ''}`] ?? at?.teile[`${o.id}_wasser`];
  if (!im || !part) return null;
  const hor = o.hor ?? 250;
  const h = 49;
  const c = document.createElement('canvas');
  c.width = d.breite;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(im, part[0] + d.rand, part[1] + hor + 2, d.breite, h, 0, 0, d.breite, h);
  const px = g.getImageData(0, 0, d.breite, h).data;
  let r = 0;
  let gg = 0;
  let b = 0;
  let n = 0;
  for (let i = 0; i < px.length; i += 4) {
    const a = px[i + 3]! / 255;
    r += px[i]! * a;
    gg += px[i + 1]! * a;
    b += px[i + 2]! * a;
    n += a;
  }
  const out: RGB | null = n > 0 ? [r / n, gg / n, b / n] : null;
  waterMeans.set(key, out);
  return out;
}

/** Wie weit eine Zeile gezogen ist: Strömung plus Wind, hinten langsamer */
const rowPath = (st: SeaState, i: number, weg: number) => (weg + st.drift) * st.zug[i]!;

export type SeaInput = {
  sg: Seegang;
  d: Durchfahrt;
  o: Ort;
  /** Seegang der Sturmstufe, 0 bis 1 */
  see: number;
  wind: number;
  /** Weg des Wassers in Pixeln (unten, im Tempo der Ebene vorn; nach rechts positiv) */
  weg: number;
  now: number;
};

/** Phasen weiterlaufen lassen (jedes Bild) */
function advance(st: SeaState, s: SeaInput): void {
  const dt = Number.isFinite(st.lastNow) ? clamp(s.now - st.lastNow, 0, 0.1) : 0;
  st.lastNow = s.now;
  if (dt <= 0) return;
  const { sg, see, wind } = s;
  const speed = 1.05 * (1 + 0.9 * wind) * (0.75 + 0.45 * see);
  sg.zuege.forEach(([, len], j) => {
    const lambda = sg.laenge * len;
    st.ph[j] = (st.ph[j] ?? 0) + dt * ((2 * Math.PI) / lambda) * Math.sqrt(lambda) * speed;
  });
  st.drift += dt * (6 + 22 * wind) * see;
  st.gph += dt * ((2 * Math.PI) / (6.5 * sg.laenge)) * 0.5 * Math.sqrt(sg.laenge) * speed;
}

/** Den Seegang des Orts neu rechnen (Hinweise „Seegang, Wellenzüge“, „ausblenden und Höhe“, „Stufen und Farben“) */
function compute(st: SeaState, s: SeaInput, sky: RGB, water: RGB): void {
  const { sg, d, see } = s;
  const n = st.p.length;
  const W = d.breite + 2 * MARGIN;
  st.canvas ??= document.createElement('canvas');
  const c = st.canvas;
  if (c.width !== W || c.height !== n) {
    c.width = W;
    c.height = n;
  }
  if (!st.hn || st.hn.length !== W * n) st.hn = new Float32Array(W * n);
  const hnBuf = st.hn;
  const g = c.getContext('2d')!;
  const data = g.createImageData(W, n);
  const px = data.data;
  const F = sg.farben;
  const a = 0.45 + 0.55 * see;
  const hi = mix(sky, F.himmelGrau, 0.35);
  const colors: [RGB, number][] = [
    [mix(water, F.dunkel, F.tiefesTal[0]), F.tiefesTal[1] * a],
    [mix(water, F.dunkel, F.tal[0]), F.tal[1] * a],
    [mix(water, hi, F.ruecken[0]), F.ruecken[1] * a],
    [mix(water, hi, F.kamm[0]), F.kamm[1] * a],
    [F.schaum, Math.min(0.9, 0.35 + 0.6 * see)],
  ];
  const kammAb = 0.62 - 0.12 * see;
  const schaumAb = 0.82 - 0.22 * see;
  const amp = 0.78 + 0.72 * see;
  const trains = sg.zuege.map(([deg, len, height, phase], j) => {
    const lambda = sg.laenge * len;
    const th = (deg * Math.PI) / 180;
    return { lambda, k: (2 * Math.PI) / lambda, c: Math.cos(th), s: Math.sin(th), a: height * (1 + (0.7 * see * j) / 4), phase };
  });
  const gk = (2 * Math.PI) / (6.5 * sg.laenge);
  const y0 = st.hor + 2;
  const A = new Float64Array(trains.length);
  const K = new Float64Array(trains.length);
  const al = new Float64Array(trains.length);
  const be = new Float64Array(trains.length);
  for (let i = 0; i < n; i++) {
    const p = st.p[i]!;
    const v = st.v[i]!;
    const wr = rowPath(st, i, s.weg);
    const y = y0 + i;
    let sumA = 0;
    let sumK = 0;
    trains.forEach((t, j) => {
      // Ein Zug, der in dieser Zeile zu klein für Pixel wäre, blendet aus
      const lx = (t.lambda * p) / Math.max(0.2, Math.abs(t.c));
      const ly = (t.lambda * p * p) / (1.6 * Math.max(0.15, Math.abs(t.s)));
      const blende = clamp((Math.min(lx, ly) - 3.5) / 4, 0, 1);
      A[j] = t.a * blende;
      K[j] = A[j]! * t.k * t.s;
      sumA += A[j]!;
      sumK += Math.abs(K[j]!);
      // φ = k · (cos θ · u + sin θ · v) + Phase − ph mit u = (x − w) / p, als α · x + β
      al[j] = (t.k * t.c) / p;
      be[j] = t.k * (t.s * v - (t.c * wr) / p) + t.phase - st.ph[j]!;
    });
    const row = i * W;
    if (sumA <= 1e-6) {
      hnBuf.fill(0, row, row + W);
      continue;
    }
    const step = p >= 0.9 ? 1 : 2;
    const fine = Math.min(1, 1.6 * p) * amp;
    const bay = BAYER4[y % 4]!;
    for (let xs = 0; xs < W; xs += step) {
      const x = xs - MARGIN;
      let h = 0;
      let sn = 0;
      for (let j = 0; j < trains.length; j++) {
        const ph = al[j]! * x + be[j]!;
        h += A[j]! * Math.sin(ph);
        sn += K[j]! * Math.cos(ph);
      }
      h /= sumA;
      sn = sumK > 1e-9 ? sn / sumK : 0;
      const u = (x - wr) / p;
      const gr = 0.56 + 0.44 * Math.sin(gk * (u + 0.35 * v) - st.gph);
      const hn = h * gr;
      const snn = sn * gr;
      for (let xx = xs; xx < Math.min(W, xs + step); xx++) {
        const X = xx - MARGIN;
        hnBuf[row + xx] = hn;
        const q = (0.8 * hn + 0.45 * snn) * fine + (bay[((X % 4) + 4) % 4]! / 16 - 0.47) * 0.32;
        let level = -1;
        if (see > 0.3 && hn > schaumAb) {
          // Schaum nach einem festen Muster, das mit dem Wasser zieht
          const xw = Math.floor(X - wr);
          const r = (Math.imul((Math.imul(xw, 374761393) + Math.imul(y, 668265263)) | 0, 1274126177) >>> 0) / 4294967296;
          if (r < 0.38 + (0.5 * see * (hn - schaumAb)) / (1 - schaumAb + 0.01) + 0.25 * see) level = 4;
        }
        if (level < 0) {
          if ((q >= sg.stufen.kamm && hn >= 0.6 * kammAb) || (hn > kammAb && q > 0)) level = 3;
          else if (q < sg.stufen.tiefesTal) level = 0;
          else if (q < sg.stufen.tal) level = 1;
          else if (q >= sg.stufen.ruecken) level = 2;
        }
        if (level < 0) continue;
        const [col, alpha] = colors[level]!;
        const o = (row + xx) * 4;
        px[o] = col[0];
        px[o + 1] = col[1];
        px[o + 2] = col[2];
        px[o + 3] = Math.round(255 * alpha);
      }
    }
  }
  g.putImageData(data, 0, 0);
}

/**
 * Den Seegang eines Orts zeichnen (nach der Ebene wasser, vor der Ebene dinge). Rückgabe: ob gezeichnet wurde. Ohne
 * Seegang (see 0) bleibt das Wasser, wie es ist.
 */
export function drawSea(ctx: CanvasRenderingContext2D, s: SeaInput): boolean {
  const { sg, d, o } = s;
  const st = stateOf(o.id, sg);
  advance(st, s);
  if (s.see <= 0.001) {
    st.hn = null;
    return false;
  }
  const hor = o.hor ?? 250;
  rowsOf(st, hor, sg);
  const n = st.p.length;
  const i80 = Math.round(0.8 * (n - 1));
  const now80 = rowPath(st, i80, s.weg);
  st.shift = Math.round(now80 - st.done80);
  if (!st.canvas || s.now - st.doneAt >= sg.takt / 1000 || s.now < st.doneAt || Math.abs(st.shift) > MARGIN - 4) {
    const water = waterColor(d, o, true);
    if (!water) return false;
    compute(st, s, hexRgb(o.himmel), water);
    st.doneAt = s.now;
    st.done80 = now80;
    st.shift = 0;
  }
  ctx.drawImage(st.canvas!, st.shift - MARGIN, hor + 2);
  return true;
}

/** hn des Seegangs an einem Punkt des Bilds (0 ohne Seegang), für die Wasserkante am Rumpf */
export function seaHeight(o: Ort, x: number, y: number): number {
  const st = states.get(o.id);
  if (!st?.hn || !st.p.length) return 0;
  const i = Math.round(y) - (st.hor + 2);
  const xx = Math.round(x) - st.shift + MARGIN;
  const W = st.hn.length / st.p.length;
  if (i < 0 || i >= st.p.length || xx < 0 || xx >= W) return 0;
  return st.hn[i * W + xx]!;
}

/** Ein in x gestrecktes Rauschen für die Strähnen im Dunst, einmal gerechnet, waagrecht nahtlos */
let streaks: HTMLCanvasElement | null = null;
function streakCanvas(w: number, h: number): HTMLCanvasElement {
  if (streaks && streaks.width === w && streaks.height === h) return streaks;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  const im = g.createImageData(w, h);
  // Gitterwerte, waagrecht 40 Pixel, senkrecht 3 Pixel auseinander
  const gx = Math.max(1, Math.round(w / 40));
  const gy = Math.ceil(h / 3) + 2;
  const hash = (i: number, j: number) => (Math.imul(Math.imul(((i % gx) + gx) % gx, 73856093) ^ Math.imul(j, 19349663), 83492791) >>> 0) / 4294967296;
  const sm = (t: number) => t * t * (3 - 2 * t);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const fx = (x / w) * gx;
      const fy = y / 3;
      const i = Math.floor(fx);
      const j = Math.floor(fy);
      const tx = sm(fx - i);
      const ty = sm(fy - j);
      const top = hash(i, j) + (hash(i + 1, j) - hash(i, j)) * tx;
      const bot = hash(i, j + 1) + (hash(i + 1, j + 1) - hash(i, j + 1)) * tx;
      const val = top + (bot - top) * ty;
      // Strähnen: nur die hellen Stellen, in der Mitte des Bands am dichtesten
      const env = Math.sin(Math.PI * clamp(y / (h - 1), 0, 1));
      const a = clamp((val - 0.5) / 0.3, 0, 1) * env;
      setWhite(im.data, (y * w + x) * 4, a);
    }
  g.putImageData(im, 0, 0);
  streaks = c;
  return c;
}
function setWhite(data: Uint8ClampedArray, o: number, a: number): void {
  data[o] = 255;
  data[o + 1] = 255;
  data[o + 2] = 255;
  data[o + 3] = Math.round(255 * a);
}

/**
 * Dunst über dem Wasser am Horizont (Hinweis „Dicht und Eingeholt deutlich verschieden“, dunst): ein Verlauf in Grau mit
 * dem Himmel des Orts, dazu Strähnen, die mit dem Wind nach rechts ziehen
 */
export function drawHaze(ctx: CanvasRenderingContext2D, d: Durchfahrt, o: Ort, dunst: number, wind: number, now: number): void {
  if (dunst <= 0.002) return;
  const hor = o.hor ?? 250;
  const col = mix([150, 158, 168], hexRgb(o.himmel), 0.5).map(Math.round) as RGB;
  const top = hor - 6;
  const h = 52;
  const g = ctx.createLinearGradient(0, top, 0, top + h);
  const c = (a: number) => `rgba(${col[0]}, ${col[1]}, ${col[2]}, ${clamp(a, 0, 1)})`;
  g.addColorStop(0, c(0));
  g.addColorStop(0.1, c(dunst));
  g.addColorStop(0.4, c(0.35 * dunst));
  g.addColorStop(1, c(0));
  ctx.fillStyle = g;
  ctx.fillRect(0, top, d.breite, h);
  // Strähnen auf 40 % der Höhe des Bands
  const sh = Math.round(h * 0.4);
  const sc = streakCanvas(d.breite, sh);
  const tinted = tintStreaks(sc, col);
  const off = Math.round((now * (6 + 30 * wind)) % d.breite);
  ctx.save();
  ctx.globalAlpha = clamp(0.5 * dunst, 0, 1);
  const y = top + Math.round(h * 0.06);
  ctx.drawImage(tinted, off, y);
  ctx.drawImage(tinted, off - d.breite, y);
  ctx.restore();
}
const tintCache = { key: '', c: null as HTMLCanvasElement | null };
function tintStreaks(src: HTMLCanvasElement, col: RGB): HTMLCanvasElement {
  const key = `${col.join(',')}|${src.width}x${src.height}`;
  if (tintCache.c && tintCache.key === key) return tintCache.c;
  const c = tintCache.c ?? document.createElement('canvas');
  c.width = src.width;
  c.height = src.height;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, c.width, c.height);
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = `rgb(${col[0]}, ${col[1]}, ${col[2]})`;
  g.fillRect(0, 0, c.width, c.height);
  g.globalCompositeOperation = 'source-over';
  tintCache.key = key;
  tintCache.c = c;
  return c;
}

/** Unterste deckende Zeile + 1 je Spalte eines Bilds (die erste Zeile Wasser unter dem Rumpf), zwischengespeichert */
const hullRows = new Map<string, Int16Array>();
export function hullBottom(src: HTMLImageElement): Int16Array {
  const key = src.src;
  const done = hullRows.get(key);
  if (done) return done;
  const w = src.naturalWidth;
  const h = src.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(src, 0, 0);
  const data = g.getImageData(0, 0, w, h).data;
  const out = new Int16Array(w).fill(-1);
  for (let x = 0; x < w; x++)
    for (let y = h - 1; y >= 0; y--)
      if (data[(y * w + x) * 4 + 3]! > 128) {
        out[x] = y + 1;
        break;
      }
  hullRows.set(key, out);
  return out;
}
